import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import {
  CountryPolicy,
  CountryPolicyStats,
  CountryRef,
  CountryUserStats,
  CountryVerificationStats,
  PrefixStats,
} from 'src/app/models/country-policy.model';
import { CountryPoliciesService } from 'src/app/services/country-policies.service';
import { ConfirmationService } from 'src/app/lib/services/confirmation.service';
import { ToastsService } from 'src/app/lib/services/toasts.service';

/** How a country stands under one control. */
export type CellState = 'blocked' | 'allowed' | 'off';

/** The country-wide action cycles through these three. */
export type OverallState = 'blocked' | 'allowed' | 'nothing';

interface WorldMap {
  width: number;
  height: number;
  countries: { [iso3: string]: string };
}

interface CountryRow extends CountryRef {
  states: { [policyKey: string]: CellState };
  overall: OverallState;
  stats: CountryVerificationStats | null;
  userStats: CountryUserStats | null;
}

/**
 * Block / Allow — every country-level control the API enforces, in one place.
 *
 * Two views over the same drafts: a world map for "what does our coverage look
 * like", and a list grouped by continent with one row per country and one
 * action per control for the actual editing. The markets that matter — the
 * Philippines and the nineteen largest Filipino populations abroad — are pinned
 * above the continents so nobody hunts for Saudi Arabia among 245 rows.
 *
 * Edits are staged and saved explicitly: changing a fraud control should be one
 * deliberate save with a diff in front of you, not a stream of silent writes.
 */
@Component({
  standalone: false,
  selector: 'sa-block-allow',
  templateUrl: './block-allow.component.html',
  styleUrls: ['./block-allow.component.scss'],
})
export class BlockAllowComponent implements OnInit {
  constructor(
    private countryPoliciesService: CountryPoliciesService,
    private confirmationService: ConfirmationService,
    private toastsService: ToastsService,
    private http: HttpClient,
  ) {}

  /** Short column headers; the full label stays in the tooltip. */
  readonly policyShortLabels: { [key: string]: string } = {
    otp_destination_allowlist: 'Twilio / OTP',
    ip_geoblock: 'IP',
    phone_country_block: 'Phone',
    location_silent_hide: 'Location',
    born_country_ban: 'Born',
    user_isolation: 'Isolation',
    otp_high_cost_prefixes: 'High-cost prefixes',
    phone_carrier_block: 'Blocked carriers',
  };

  /** Which controls the country-wide action writes to. */
  private readonly blockPolicies = ['ip_geoblock', 'phone_country_block', 'location_silent_hide'];
  private readonly allowPolicy = 'otp_destination_allowlist';

  policies: CountryPolicy[] = [];

  /** Draft text for the "add a value" box on each non-country list. */
  newValue: { [policyKey: string]: string } = {};
  countries: CountryRef[] = [];
  world: WorldMap | null = null;

  /** 'map', 'list', or a policy key — each control gets its own tab. */
  view = 'map';

  stats: CountryPolicyStats | null = null;
  /** Country hovered on the map, for the tooltip. */
  hovered: CountryRow | null = null;
  hoverX = 0;
  hoverY = 0;
  isLoading = true;
  isSaving = false;
  hasError = false;

  search = '';
  selectedIso2: string | null = null;

  /** policyKey -> staged set of codes. */
  private draft: { [policyKey: string]: Set<string> } = {};
  private draftEnabled: { [policyKey: string]: boolean } = {};

  ngOnInit() {
    this.load();
  }

  private load() {
    this.isLoading = true;

    forkJoin({
      policies: this.countryPoliciesService.getPolicies(),
      world: this.http.get<WorldMap>('assets/world-countries.json')
        .pipe(catchError(() => of(null as WorldMap))),
      // Stats are a nice-to-have: if the aggregate fails the page still works.
      stats: this.countryPoliciesService.getStats(7)
        .pipe(catchError(() => of(null as CountryPolicyStats))),
    }).pipe(
      tap(({ policies, world, stats }) => {
        this.policies = policies.policies || [];
        this.countries = policies.countries || [];
        this.world = world;
        this.stats = stats;
        this.indexStats();
        this.resetBox();
        this.resetDrafts();
        this.isLoading = false;
        this.hasError = false;
      }),
      catchError(() => {
        this.isLoading = false;
        this.hasError = true;
        return of(null);
      }),
    ).subscribe();
  }

  /** Verification stats key on ISO-2, user stats on ISO-3 — never the same map. */
  private statsByIso2: { [iso2: string]: CountryVerificationStats } = {};
  private usersByIso3: { [iso3: string]: CountryUserStats } = {};

  private indexStats() {
    this.statsByIso2 = {};
    this.usersByIso3 = {};
    if (!this.stats) {
      return;
    }
    (this.stats.countries || []).forEach((row) => {
      this.statsByIso2[row.country_iso2] = row;
    });
    (this.stats.users || []).forEach((row) => {
      this.usersByIso3[row.country_iso3] = row;
    });
  }

  get prefixStats(): PrefixStats[] {
    const rows = (this.stats && this.stats.prefixes) || [];
    // Busiest first: a prefix nothing dials is not the one to look at.
    return rows.slice().sort((a, b) => b.attempts - a.attempts || a.prefix.localeCompare(b.prefix));
  }

  get statsWindowDays(): number {
    return this.stats ? this.stats.window_days : 7;
  }

  private resetDrafts() {
    this.draft = {};
    this.draftEnabled = {};
    this.policies.forEach((policy) => {
      this.draft[policy.key] = new Set(policy.countries);
      this.draftEnabled[policy.key] = policy.enabled;
    });
  }

  /** Lists keyed on a country — these get a column and a map colour. */
  get countryPolicies(): CountryPolicy[] {
    return this.policies.filter(policy => policy.code_type === 'iso2' || policy.code_type === 'iso3');
  }

  /**
   * Lists that block something other than a country: premium dial prefixes and
   * carrier names. Same override model, edited as chips rather than a grid.
   */
  get valuePolicies(): CountryPolicy[] {
    return this.policies.filter(policy => policy.code_type === 'prefix' || policy.code_type === 'carrier');
  }

  /** The control whose own tab is open, if any. */
  get activePolicy(): CountryPolicy | null {
    return this.policies.find(policy => policy.key === this.view) || null;
  }

  get prefixPolicy(): CountryPolicy | null {
    return this.policies.find(policy => policy.code_type === 'prefix') || null;
  }

  /**
   * Countries as one control sees them: pinned markets first, then everything
   * else alphabetically, filtered by the search box.
   */
  get policyRows(): CountryRow[] {
    const policy = this.activePolicy;
    if (!policy) {
      return [];
    }
    return this.countries
      .filter(country => this.matchesSearch(country))
      .filter(country => Boolean(this.codeFor(country, policy)))
      .sort((a, b) => {
        const ra = a.market_rank || 999;
        const rb = b.market_rank || 999;
        return ra - rb || a.name.localeCompare(b.name);
      })
      .map(country => this.buildRow(country));
  }

  /** Add or remove every country currently listed under the open control. */
  addAllShown() {
    const policy = this.activePolicy;
    if (!policy) return;
    this.policyRows.forEach((row) => {
      const code = this.codeFor(row, policy);
      if (code) this.draft[policy.key].add(code);
    });
  }

  removeAllShown() {
    const policy = this.activePolicy;
    if (!policy) return;
    this.policyRows.forEach((row) => {
      const code = this.codeFor(row, policy);
      if (code) this.draft[policy.key].delete(code);
    });
  }

  /** "15 (23%)" — the count with its share of the country's users. */
  share(part: number | undefined, total: number | undefined): string {
    const n = Number(part) || 0;
    const t = Number(total) || 0;
    if (!t) {
      return `${n}`;
    }
    return `${n.toLocaleString()} (${Math.round((n / t) * 100)}%)`;
  }

  /**
   * Prefixes as the tab shows them: every staged prefix, carrying whatever
   * traffic the API measured for it. A prefix added but not yet saved shows
   * with zeroes rather than disappearing.
   */
  get prefixRows(): PrefixStats[] {
    const policy = this.prefixPolicy;
    if (!policy) {
      return [];
    }
    const measured: { [prefix: string]: PrefixStats } = {};
    this.prefixStats.forEach((row) => { measured[row.prefix] = row; });

    return this.valuesOf(policy.key)
      .map(prefix => measured[prefix] || {
        prefix, attempts: 0, attempts_recent: 0, numbers: 0,
      })
      .sort((a, b) => b.attempts - a.attempts || a.prefix.localeCompare(b.prefix));
  }

  valuesOf(policyKey: string): string[] {
    return Array.from(this.draft[policyKey] || []).sort();
  }

  addValue(policy: CountryPolicy) {
    const raw = (this.newValue[policy.key] || '').trim();
    if (!raw) {
      return;
    }
    // Prefixes are canonicalised here too, so the chip reads how it will save.
    const value = policy.code_type === 'prefix'
      ? (raw.startsWith('+') ? raw : `+${raw}`)
      : raw;
    this.draft[policy.key].add(value);
    this.newValue[policy.key] = '';
  }

  removeValue(policyKey: string, value: string) {
    this.draft[policyKey].delete(value);
  }

  codeFor(country: CountryRef, policy: CountryPolicy): string | null {
    return policy.code_type === 'iso3' ? country.iso3 : country.iso2;
  }

  private stateFor(country: CountryRef, policy: CountryPolicy): CellState {
    const code = this.codeFor(country, policy);
    if (!code) {
      return 'off';
    }
    const onList = this.draft[policy.key].has(code);
    if (policy.effect === 'allow') {
      return onList ? 'allowed' : 'blocked';
    }
    return onList ? 'blocked' : 'off';
  }

  /**
   * One verdict per country for the map: blocked if any control blocks it,
   * allowed if it is explicitly on the allowlist and nothing blocks it.
   */
  private overallFor(country: CountryRef): OverallState {
    const blocked = this.countryPolicies.some(policy => policy.effect === 'block'
      && this.stateFor(country, policy) === 'blocked');
    if (blocked) {
      return 'blocked';
    }
    const allowPolicy = this.policies.find(policy => policy.key === this.allowPolicy);
    if (allowPolicy && this.stateFor(country, allowPolicy) === 'allowed') {
      return 'allowed';
    }
    return 'nothing';
  }

  private buildRow(country: CountryRef): CountryRow {
    const states: { [policyKey: string]: CellState } = {};
    this.countryPolicies.forEach((policy) => { states[policy.key] = this.stateFor(country, policy); });
    return {
      ...country,
      states,
      overall: this.overallFor(country),
      stats: this.statsByIso2[country.iso2] || null,
      userStats: country.iso3 ? this.usersByIso3[country.iso3] || null : null,
    };
  }

  matchesSearch(country: CountryRef): boolean {
    const term = this.search.trim().toLowerCase();
    if (!term) {
      return true;
    }
    return country.name.toLowerCase().includes(term)
      || country.iso2.toLowerCase().includes(term)
      || (country.iso3 || '').toLowerCase().includes(term)
      || (country.calling_code || '').includes(term);
  }

  /** The Philippines and the nineteen largest Filipino populations abroad, in order. */
  get pinnedRows(): CountryRow[] {
    return this.countries
      .filter(country => country.market_rank && this.matchesSearch(country))
      .sort((a, b) => (a.market_rank || 0) - (b.market_rank || 0))
      .map(country => this.buildRow(country));
  }

  get continentGroups(): { continent: string; rows: CountryRow[] }[] {
    const groups: { [continent: string]: CountryRow[] } = {};

    this.countries
      .filter(country => this.matchesSearch(country))
      .forEach((country) => {
        const continent = country.continent || 'Other';
        if (!groups[continent]) {
          groups[continent] = [];
        }
        groups[continent].push(this.buildRow(country));
      });

    return Object.keys(groups)
      .sort()
      .map(continent => ({
        continent,
        rows: groups[continent].sort((a, b) => a.name.localeCompare(b.name)),
      }));
  }

  /** One SVG path per country, coloured by its overall verdict. */
  get mapShapes(): { iso3: string; iso2: string; name: string; path: string; overall: OverallState }[] {
    if (!this.world) {
      return [];
    }
    const byIso3: { [iso3: string]: CountryRef } = {};
    this.countries.forEach((country) => {
      if (country.iso3) {
        byIso3[country.iso3] = country;
      }
    });

    return Object.keys(this.world.countries).map((iso3) => {
      const country = byIso3[iso3];
      return {
        iso3,
        iso2: country ? country.iso2 : '',
        name: country ? country.name : iso3,
        path: this.world.countries[iso3],
        overall: country ? this.overallFor(country) : 'nothing',
      };
    });
  }

  get counts(): { blocked: number; allowed: number; nothing: number } {
    const counts = { blocked: 0, allowed: 0, nothing: 0 };
    this.countries.forEach((country) => { counts[this.overallFor(country)] += 1; });
    return counts;
  }

  get selectedRow(): CountryRow | null {
    if (!this.selectedIso2) {
      return null;
    }
    const country = this.countries.find(item => item.iso2 === this.selectedIso2);
    return country ? this.buildRow(country) : null;
  }

  // ---- map zoom / pan -----------------------------------------------------
  //
  // The whole world in one 1000x500 box makes Luxembourg unclickable, so the
  // map pans and zooms by moving the viewBox. Everything stays vector, so no
  // tiles are fetched and the shapes stay sharp at any zoom.

  /** Current viewBox; starts as the whole world. */
  box = { x: 0, y: 0, w: 1000, h: 500 };

  private readonly maxZoom = 24;
  private panning = false;
  private panPointer = { x: 0, y: 0 };
  /** How far the pointer travelled while down, so a drag is not read as a click. */
  private panDistance = 0;

  get viewBoxAttr(): string {
    return `${this.box.x} ${this.box.y} ${this.box.w} ${this.box.h}`;
  }

  get zoomLevel(): number {
    const full = this.world ? this.world.width : 1000;
    return full / this.box.w;
  }

  private resetBox() {
    this.box = {
      x: 0,
      y: 0,
      w: this.world ? this.world.width : 1000,
      h: this.world ? this.world.height : 500,
    };
  }

  /** Clamp so the map cannot be zoomed past its own edges or lost off-screen. */
  private clampBox() {
    const fullW = this.world ? this.world.width : 1000;
    const fullH = this.world ? this.world.height : 500;
    const minW = fullW / this.maxZoom;

    this.box.w = Math.min(Math.max(this.box.w, minW), fullW);
    this.box.h = this.box.w * (fullH / fullW);
    this.box.x = Math.min(Math.max(this.box.x, 0), fullW - this.box.w);
    this.box.y = Math.min(Math.max(this.box.y, 0), fullH - this.box.h);
  }

  /**
   * Zoom by a factor, keeping the point under the cursor where it is — the
   * behaviour every map has, and the reason zoom-to-centre feels broken.
   */
  private zoomAt(factor: number, clientX?: number, clientY?: number) {
    const svg = this.mapElement;
    const rect = svg ? svg.getBoundingClientRect() : null;

    // Fraction of the viewport the anchor sits at (centre when we have no cursor).
    const fx = rect && clientX !== undefined ? (clientX - rect.left) / rect.width : 0.5;
    const fy = rect && clientY !== undefined ? (clientY - rect.top) / rect.height : 0.5;

    const anchorX = this.box.x + this.box.w * fx;
    const anchorY = this.box.y + this.box.h * fy;

    this.box.w /= factor;
    this.box.h /= factor;
    this.box.x = anchorX - this.box.w * fx;
    this.box.y = anchorY - this.box.h * fy;
    this.clampBox();
  }

  private get mapElement(): SVGSVGElement | null {
    return document.querySelector('svg.world') as SVGSVGElement | null;
  }

  onWheel(event: WheelEvent) {
    event.preventDefault();
    this.zoomAt(event.deltaY < 0 ? 1.2 : 1 / 1.2, event.clientX, event.clientY);
  }

  zoomIn() {
    this.zoomAt(1.4);
  }

  zoomOut() {
    this.zoomAt(1 / 1.4);
  }

  resetZoom() {
    this.resetBox();
  }

  onPanStart(event: MouseEvent) {
    this.panning = true;
    this.panDistance = 0;
    this.panPointer = { x: event.clientX, y: event.clientY };
  }

  onPanMove(event: MouseEvent) {
    if (!this.panning) {
      return;
    }
    const svg = this.mapElement;
    const rect = svg ? svg.getBoundingClientRect() : null;
    if (!rect) {
      return;
    }

    const dx = event.clientX - this.panPointer.x;
    const dy = event.clientY - this.panPointer.y;
    this.panDistance += Math.abs(dx) + Math.abs(dy);
    this.panPointer = { x: event.clientX, y: event.clientY };

    // Drag moves the map with the pointer, so translate the viewBox the other way.
    this.box.x -= dx * (this.box.w / rect.width);
    this.box.y -= dy * (this.box.h / rect.height);
    this.clampBox();
  }

  onPanEnd() {
    this.panning = false;
  }

  /** True when the pointer travelled far enough that this was a drag, not a click. */
  get wasDragged(): boolean {
    return this.panDistance > 4;
  }

  hoverShape(shape: { iso3: string }, event: MouseEvent) {
    const country = this.countries.find(item => item.iso3 === shape.iso3);
    this.hovered = country ? this.buildRow(country) : null;
    this.hoverX = event.clientX;
    this.hoverY = event.clientY;
  }

  clearHover() {
    this.hovered = null;
  }

  selectCountry(iso2: string) {
    this.selectedIso2 = this.selectedIso2 === iso2 ? null : iso2;
  }

  selectByIso3(iso3: string) {
    if (this.wasDragged) {
      return;
    }
    const country = this.countries.find(item => item.iso3 === iso3);
    if (country) {
      this.selectCountry(country.iso2);
    }
  }

  /** Toggle one country under one control. */
  toggleCell(country: CountryRef, policyKey: string) {
    const policy = this.policies.find(item => item.key === policyKey);
    if (!policy) {
      return;
    }
    const code = this.codeFor(country, policy);
    if (!code) {
      return;
    }
    if (this.draft[policy.key].has(code)) {
      this.draft[policy.key].delete(code);
    } else {
      this.draft[policy.key].add(code);
    }
  }

  /**
   * The country-wide action, cycling nothing -> allowed -> blocked.
   *
   *   blocked  on every block list, off the OTP allowlist
   *   allowed  off every block list, on the OTP allowlist
   *   nothing  no explicit entry anywhere (the OTP allowlist still refuses it,
   *            because absence from an allowlist is a refusal)
   */
  cycleCountry(country: CountryRef) {
    const order: OverallState[] = ['nothing', 'allowed', 'blocked'];
    const next = order[(order.indexOf(this.overallFor(country)) + 1) % order.length];
    this.applyOverall(country, next);
  }

  applyOverall(country: CountryRef, state: OverallState) {
    this.countryPolicies.forEach((policy) => {
      const code = this.codeFor(country, policy);
      if (!code) {
        return;
      }

      if (policy.key === this.allowPolicy) {
        if (state === 'allowed') {
          this.draft[policy.key].add(code);
        } else {
          this.draft[policy.key].delete(code);
        }
        return;
      }

      if (this.blockPolicies.indexOf(policy.key) === -1) {
        return;
      }

      if (state === 'blocked') {
        this.draft[policy.key].add(code);
      } else {
        this.draft[policy.key].delete(code);
      }
    });
  }

  /** How many codes the staged list holds — shown on each control card. */
  draftCount(policyKey: string): number {
    const staged = this.draft[policyKey];
    return staged ? staged.size : 0;
  }

  enabledFor(policyKey: string): boolean {
    return this.draftEnabled[policyKey];
  }

  setEnabled(policyKey: string, value: boolean) {
    this.draftEnabled[policyKey] = value;
  }

  /** Only the policies whose staged list or switch actually changed. */
  get changedPolicies(): { policy: CountryPolicy; added: string[]; removed: string[]; toggled: boolean }[] {
    return this.policies
      .map((policy) => {
        const before = new Set(policy.countries);
        const staged = this.draft[policy.key] || new Set<string>();
        const added = Array.from(staged).filter(code => !before.has(code)).sort();
        const removed = policy.countries.filter(code => !staged.has(code)).sort();
        const toggled = this.draftEnabled[policy.key] !== policy.enabled;
        return { policy, added, removed, toggled };
      })
      .filter(change => change.added.length || change.removed.length || change.toggled);
  }

  get isDirty(): boolean {
    return this.changedPolicies.length > 0;
  }

  revert() {
    this.resetDrafts();
  }

  save() {
    const changes = this.changedPolicies;
    if (!changes.length || this.isSaving) {
      return;
    }

    const summary = changes.map((change) => {
      const parts = [
        change.added.length ? `+${change.added.length}` : '',
        change.removed.length ? `−${change.removed.length}` : '',
        change.toggled ? (this.draftEnabled[change.policy.key] ? 'switched on' : 'switched off') : '',
      ].filter(Boolean).join(' ');
      return `${change.policy.label} (${parts})`;
    }).join(', ');

    this.confirmationService.askForConfirmation(
      'Save country policy changes?',
      `This changes what the live API enforces straight away: ${summary}.`,
    ).then(() => {
      this.isSaving = true;
      this.countryPoliciesService.savePolicies(changes.map(change => ({
        policy: change.policy.key,
        countries: Array.from(this.draft[change.policy.key]),
        enabled: this.draftEnabled[change.policy.key],
      }))).pipe(
        tap(() => {
          this.isSaving = false;
          this.toastsService.showSuccess('Country policies saved.');
          this.load();
        }),
        catchError((error) => {
          this.isSaving = false;
          this.toastsService.showDanger(`Could not save: ${error?.error?.error || JSON.stringify(error)}`);
          return of(null);
        }),
      ).subscribe();
    }).catch(() => {});
  }

  resetPolicyToDefault(policy: CountryPolicy) {
    this.confirmationService.askForConfirmation(
      `Reset ${policy.label} to the committed default?`,
      'The dashboard override is dropped and the list in the repository applies again.',
    ).then(() => {
      this.isSaving = true;
      this.countryPoliciesService.resetPolicy(policy.key).pipe(
        tap(() => {
          this.isSaving = false;
          this.toastsService.showSuccess(`${policy.label} reset to the committed default.`);
          this.load();
        }),
        catchError((error) => {
          this.isSaving = false;
          this.toastsService.showDanger(`Could not reset: ${JSON.stringify(error)}`);
          return of(null);
        }),
      ).subscribe();
    }).catch(() => {});
  }

  trackByIso2(_index: number, row: CountryRow): string {
    return row.iso2;
  }

  trackByIso3(_index: number, shape: { iso3: string }): string {
    return shape.iso3;
  }

  trackByKey(_index: number, policy: CountryPolicy): string {
    return policy.key;
  }
}
